/* eslint-disable max-params, default-param-last, prefer-destructuring */
import {
	BufferGeometry,
	Line,
	LineBasicMaterial,
	LineLoop,
	Mesh,
	MeshStandardMaterial,
	Scene,
	SphereGeometry,
	Vector3
} from 'three';

/**
 * Color constants for debug objects
 */
export const DebugColors = {
	/** Dark yellow for default position markers */
	DARK_YELLOW: 0xcccc00,
	/** Light yellow for target markers */
	LIGHT_YELLOW: 0xffffcc,
	/** Bright yellow for source markers */
	YELLOW: 0xffff00,
	/** Orange for transition arcs */
	ORANGE: 0xff8800,
	/** Highlight circle color */
	HIGHLIGHT_CIRCLE: 0xffff00
} as const;

/**
 * Size constants for debug objects (in inches)
 */
export const DebugSizes = {
	/** Standard sphere diameter (2") */
	SPHERE_DIAMETER: 2,
	/** Vector line length (18") - perpendicular to face */
	VECTOR_LENGTH: 18
} as const;

/**
 * Creates a debug sphere
 *
 * @param position Initial position
 * @param color Sphere color (hex)
 * @param diameter Sphere diameter in scene units
 * @param name Optional name for the mesh
 * @returns Mesh representing the sphere
 */
export const createDebugSphere = (
	position: Vector3,
	color: number,
	diameter: number = DebugSizes.SPHERE_DIAMETER,
	name?: string
): Mesh => {
	const radius = diameter / 2;
	const geometry = new SphereGeometry(radius, 16, 16);
	const material = new MeshStandardMaterial({
		color,
		emissive: color,
		emissiveIntensity: 0.3,
		wireframe: false // Interface objects never wireframe
	});
	const sphere = new Mesh(geometry, material);
	sphere.position.copy(position);
	if (name) {
		sphere.name = name;
	}
	// Mark as interface object to exclude from wireframe mode
	sphere.userData.isInterface = true;
	sphere.userData.type = 'debugVisualization';
	return sphere;
};

/**
 * Creates a debug line between two points
 *
 * @param start Start position
 * @param end End position
 * @param color Line color (hex)
 * @param name Optional name for the line
 * @returns Line object
 */
export const createDebugLine = (
	start: Vector3,
	end: Vector3,
	color: number,
	name?: string
): Line => {
	const geometry = new BufferGeometry().setFromPoints([start, end]);
	const material = new LineBasicMaterial({ color });
	const line = new Line(geometry, material);
	if (name) {
		line.name = name;
	}
	// Mark as interface object to exclude from wireframe mode
	line.userData.isInterface = true;
	line.userData.type = 'debugVisualization';
	return line;
};

/**
 * Creates a circle in the XZ plane (horizontal)
 *
 * @param center Center position of the circle
 * @param radius Circle radius
 * @param color Circle color (hex)
 * @param segments Number of segments (default: 64)
 * @param name Optional name for the circle
 * @returns LineLoop representing the circle
 */
export const createDebugCircle = (
	center: Vector3,
	radius: number,
	color: number,
	segments = 64,
	name?: string
): LineLoop => {
	const points: Vector3[] = [];
	for (let i = 0; i <= segments; i++) {
		const angle = (i / segments) * Math.PI * 2;
		points.push(new Vector3(
			Math.cos(angle) * radius,
			0,
			Math.sin(angle) * radius
		));
	}

	const geometry = new BufferGeometry().setFromPoints(points);
	const material = new LineBasicMaterial({ color });
	const circle = new LineLoop(geometry, material);
	circle.position.copy(center);
	if (name) {
		circle.name = name;
	}
	// Mark as interface object to exclude from wireframe mode
	circle.userData.isInterface = true;
	circle.userData.type = 'debugVisualization';
	return circle;
};

/**
 * Updates a debug line's endpoints
 *
 * @param line The line to update
 * @param start New start position
 * @param end New end position
 */
export const updateDebugLine = (line: Line, start: Vector3, end: Vector3): void => {
	line.geometry.dispose();
	line.geometry = new BufferGeometry().setFromPoints([start, end]);
};

/**
 * Collection of debug objects for a single highlight
 */
export interface HighlightDebugObjects {
	/** Sphere at highlight target position */
	targetSphere: Mesh | null;
	/** Sphere at highlight light source position */
	sourceSphere: Mesh | null;
	/** Line from source to target */
	pathLine: Line | null;
	/** Sphere at front face center */
	frontFaceSphere: Mesh | null;
	/** Line extending from front face */
	frontFaceVector: Line | null;
	/** Sphere at top face center */
	topFaceSphere: Mesh | null;
	/** Line extending from top face */
	topFaceVector: Line | null;
	/** Transition arc when switching highlights */
	transitionArc: Line | null;
}

/**
 * Creates an empty debug objects container
 */
export const createEmptyDebugObjects = (): HighlightDebugObjects => ({
	targetSphere: null,
	sourceSphere: null,
	pathLine: null,
	frontFaceSphere: null,
	frontFaceVector: null,
	topFaceSphere: null,
	topFaceVector: null,
	transitionArc: null
});

/**
 * Disposes and removes all debug objects from the scene
 *
 * @param debugObjects Debug objects to dispose
 * @param scene Scene to remove objects from
 */
export const disposeDebugObjects = (debugObjects: HighlightDebugObjects, scene: Scene): void => {
	const objects = [
		debugObjects.targetSphere,
		debugObjects.sourceSphere,
		debugObjects.pathLine,
		debugObjects.frontFaceSphere,
		debugObjects.frontFaceVector,
		debugObjects.topFaceSphere,
		debugObjects.topFaceVector,
		debugObjects.transitionArc
	];

	for (const obj of objects) {
		if (obj) {
			scene.remove(obj);
			obj.geometry?.dispose();
			// Dispose material - handle both Mesh and Line types
			const { material } = obj as Mesh | Line | LineLoop;
			if (material) {
				if (Array.isArray(material)) {
					material.forEach((m) => m.dispose());
				} else {
					material.dispose();
				}
			}
		}
	}

	// Reset all to null
	debugObjects.targetSphere = null;
	debugObjects.sourceSphere = null;
	debugObjects.pathLine = null;
	debugObjects.frontFaceSphere = null;
	debugObjects.frontFaceVector = null;
	debugObjects.topFaceSphere = null;
	debugObjects.topFaceVector = null;
	debugObjects.transitionArc = null;
};

/**
 * Sets visibility of all debug objects
 *
 * @param debugObjects Debug objects to update
 * @param visible Whether objects should be visible
 */
export const setDebugObjectsVisibility = (debugObjects: HighlightDebugObjects, visible: boolean): void => {
	const objects = [
		debugObjects.targetSphere,
		debugObjects.sourceSphere,
		debugObjects.pathLine,
		debugObjects.frontFaceSphere,
		debugObjects.frontFaceVector,
		debugObjects.topFaceSphere,
		debugObjects.topFaceVector,
		debugObjects.transitionArc
	];

	for (const obj of objects) {
		if (obj) {
			obj.visible = visible;
		}
	}
};

